import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import {
  getPaymentReferenceId,
  getPurchasedPlanId,
  isAutoPassPurchase,
} from '../../lib/util/url-helpers';

import { GTM_CATEGORIES, GTM_EVENTS, GTM_PAYMENT_CANCELLED } from '../../constants/gtm';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { hashSHA256 } from '../../util/hash';
import { UI_ROUTES } from '../../util/routes';
import Layout from '../Enrollment/component/Layout';

import styles from './PlanCancel.module.scss';

const PlanCancel = (): JSX.Element => {
  const navigate = useNavigate();
  const redirectUrl = sessionStorage.getItem('paymentGatewayURL');
  const hasTrackedPaymentCancelled = useRef<boolean>(false);

  const userId = useSelector((state: RootState) => state.auth.enroll.userId);
  const userEmail = useSelector((state: RootState) => state.crm.individualExtendedInfo?.email);
  const tradingPlans = useSelector((state: RootState) => state.crm.tradingPlans);
  const purchasedPlanId = getPurchasedPlanId();
  const plan = tradingPlans.find((p) => p.id === purchasedPlanId);

  // GTM: Track payment cancellation (fire only once)
  useEffect(() => {
    if (!hasTrackedPaymentCancelled.current) {
      hasTrackedPaymentCancelled.current = true;
      const paymentReferenceId = getPaymentReferenceId();
      const pushEvent = async () => {
        const hashedEmail = await hashSHA256(userEmail || '');
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.PAYMENT_CANCELLED,
            event_category: GTM_CATEGORIES.CHALLENGE_PURCHASE,
            event_action: GTM_PAYMENT_CANCELLED.ACTION,
            event_label: GTM_PAYMENT_CANCELLED.LABEL,
            client_id: userId,
            user_email_hashed: hashedEmail,
            transaction_id: paymentReferenceId,
            challenge_name: plan?.phase,
            buying_power: plan?.daily_hard_bp,
            currency: plan?.daily_hard_ccy,
          },
        });
      };
      pushEvent();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoBack = () => {
    if (redirectUrl) {
      window.open(redirectUrl, '_self');
      return;
    }
    navigate(-1);
  };

  const hangleGoToPlatform = () => {
    sessionStorage.removeItem('paymentGatewayURL');
    sessionStorage.removeItem('paymentReturnExpected');
    navigate(isAutoPassPurchase() ? UI_ROUTES.planDetails : UI_ROUTES.dashboard);
  };

  return (
    <Layout
      transparentContent
      formTitle={''}
      displayBackButton={false}
      displayFooter={false}
      displayNextButton={false}
      nextButtonText={''}
      displayLoginButton={false}
      onSubmit={() => {}}
      onClose={hangleGoToPlatform}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.text}>
            <p>{lang.proceedWithoutPaymentQuestion()}</p>
          </div>
          <div className={styles.buttons}>
            <Button
              fullWidth
              className={styles.button}
              variant={ButtonTypesEnum.Primary}
              onClick={hangleGoToPlatform}
            >
              {lang.proceedToPlatform()}
            </Button>
            <Button
              fullWidth
              className={styles.buttonGoBack}
              variant={ButtonTypesEnum.Tertiary}
              onClick={handleGoBack}
            >
              {lang.tryPaymentAgain()}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlanCancel;
